// src/components/ClientMenuSheet.js
//
// THE client menu. One unified sheet behind BOTH doors: the initials
// avatar on the provider page (/p/:joinCode) and the dock's More on
// every ClientShell page. Formerly AccountBottomSheet (the pill's private
// menu) while MobileMenu carried a second, drifted client list — the
// two disagreed on names and contents.
//
// Since 2026-10-25 it is a drawer of labeled icons (Ivan's pick "A ·
// Three things": the client home holds at most 50 words, so everything
// that left the page lives here):
//
//   Book a session (the one primary) ·
//   Bookings · Packages · Prices · Locations · Health form · Gift ·
//   Membership · Calendar sync · Offers · Messages · Profile · Settings ·
//   [Add email & password — passwordless only] · Sign out
//
// Prices, Locations and Offers open their sheets on the provider page
// (/p/:joinCode?sheet=…). Home is the dock's and the logo's.
//
// Data: the provider page passes what it already fetched (upcomingCount,
// businessName); opened from the Header with no props, the sheet fetches
// its own on first open — lazily, once, tolerant of failure (a missing
// badge, never a broken sheet).
//
// Interaction (unchanged): mounts only when open; 220ms slide-up
// gated on prefers-reduced-motion; scrim / Esc / item-tap close;
// body scroll-lock; focus restored to the opener on close. While the
// AccountUpgrade credentials sheet is stacked on top, Escape belongs
// to IT.

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  X, Calendar, Package, Tag, MapPin, ClipboardList, Gift, Sparkles, CalendarCheck,
  TicketPercent, Inbox, Settings as SettingsIcon, LogOut, Lock, Trash2,
} from 'lucide-react';
import { DateTime } from 'luxon';
import { useAuth } from '../AuthContext';
import AccountUpgrade from './AccountUpgrade';

const SHEET_MS = 220;

function initialsFrom(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function ClientMenuSheet({
  open,
  onClose,
  // Optional page-supplied context. The provider page passes these
  // from its own fetches; the Header passes none and the sheet
  // self-fetches on first open.
  profile = null,
  upcomingCount = null,
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [entered, setEntered] = useState(false);
  // Shared credentials surface — stacks above this sheet
  // (AccountUpgrade's BottomSheet renders at zIndex 70 > this z-[60]).
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  // Self-fetched fallbacks for the Header context. null = unknown →
  // the badge simply doesn't render.
  const [fetched, setFetched] = useState({ upcoming: null, intakeDue: false });
  const fetchedOnceRef = useRef(false);
  const closeButtonRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!open) { setEntered(false); return; }
    if (prefersReducedMotion()) { setEntered(true); return; }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Capture previous focus on open; restore on close.
  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = document.activeElement;
    return () => {
      const target = previouslyFocusedRef.current;
      if (target && typeof target.focus === 'function') {
        setTimeout(() => target.focus(), 0);
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open || !entered) return;
    closeButtonRef.current?.focus();
  }, [open, entered]);

  // Scroll-lock the page under the sheet.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Esc closes — unless the credentials sheet is stacked on top.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !upgradeOpen) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, upgradeOpen]);

  // Lazy self-fetch, once per mount, only for what the page didn't
  // supply. Every call tolerates failure — the sheet renders fine
  // with no badges.
  useEffect(() => {
    if (!open || fetchedOnceRef.current || !user || user.accountType !== 'CLIENT') return;
    fetchedOnceRef.current = true;
    let cancelled = false;
    if (upcomingCount == null) {
      axios.get('/api/bookings', { withCredentials: true })
        .then(res => {
          if (cancelled || !Array.isArray(res.data)) return;
          const now = Date.now();
          const n = res.data.filter(b => {
            if (b.status === 'cancelled' || b.status === 'completed') return false;
            const dt = DateTime.fromFormat(`${b.localDate} ${b.startTime || '00:00'}`, 'yyyy-MM-dd HH:mm', { zone: b.timezone || 'America/Los_Angeles' });
            return dt.isValid && dt.toMillis() >= now;
          }).length;
          setFetched(f => ({ ...f, upcoming: n }));
        })
        .catch(() => {});
    }
    // Health form: the dot shows while the provider asks for one and it
    // is missing or stale (the same test as the home page's chip).
    axios.get('/api/users/client/intake', { withCredentials: true })
      .then(res => {
        if (cancelled) return;
        const d = res.data || {};
        const due = !!d.template?.enabled && (d.status === 'none' || d.status === 'stale');
        setFetched(f => ({ ...f, intakeDue: due }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, user, upcomingCount]);

  const go = useCallback((path) => {
    onClose();
    setTimeout(() => navigate(path), 10);
  }, [navigate, onClose]);

  const handleSignOut = useCallback(async () => {
    onClose();
    try {
      if (typeof logout === 'function') {
        await logout();
      } else {
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
      }
    } catch {
      /* ignored — either way, redirect to landing */
    }
    window.location.href = '/';
  }, [logout, onClose]);

  // Test-account kill switch (Ivan, 2026-08-29): the ONE designated
  // test client can delete itself completely (server re-checks the
  // identity) so the first-timer booking flow can be re-run endlessly.
  const isTestAccount = String(user?.email || '').toLowerCase() === 'ajcics@gmail.com'
    && /^\s*seanathan\s+seagull\s*$/i.test(user?.profile?.fullName || '');
  const handleKillSwitch = useCallback(async () => {
    if (!window.confirm('Delete the Seanathan Seagull test account completely (account, bookings, verifications)? This cannot be undone.')) return;
    try {
      const res = await fetch('/api/auth/test-account', { method: 'DELETE', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { window.alert(data?.message || 'Could not delete the test account.'); return; }
    } catch (e) {
      window.alert('Network error — the test account was not deleted.');
      return;
    }
    onClose();
    window.location.href = '/';
  }, [onClose]);

  if (!open) return null;

  // The provider page this client belongs to: Prices, Locations, Offers
  // and Gift live there. Hidden without one to aim at.
  const jc = profile?.joinCode || user?.provider?.joinCode || null;
  const upcoming = upcomingCount != null ? upcomingCount : fetched.upcoming;
  const tiles = [
    { key: 'bookings', label: 'Bookings', Icon: Calendar, to: '/my-bookings',
      count: upcoming > 0 ? upcoming : null, aria: upcoming > 0 ? `Bookings, ${upcoming} upcoming` : null },
    { key: 'packages', label: 'Packages', Icon: Package, to: '/my-packages' },
    jc && { key: 'prices', label: 'Prices', Icon: Tag, to: `/p/${jc}?sheet=prices` },
    jc && { key: 'places', label: 'Locations', Icon: MapPin, to: `/p/${jc}?sheet=places` },
    { key: 'intake', label: 'Health form', Icon: ClipboardList, to: '/my-intake',
      dot: fetched.intakeDue, aria: fetched.intakeDue ? 'Health form, waiting for you' : null },
    // Gift a session — signed-in-only surface by design (the anon
    // landing page stays orientation-only, 2026-08-29).
    jc && { key: 'gift', label: 'Gift', Icon: Gift, to: `/p/${jc}/gift`, aria: 'Gift a session' },
    { key: 'membership', label: 'Membership', Icon: Sparkles, to: '/my-membership' },
    // Subscribable feed — lands on /my-bookings with the sync panel
    // auto-opened (?sync=1).
    { key: 'sync', label: 'Calendar sync', Icon: CalendarCheck, to: '/my-bookings?sync=1',
      aria: 'Calendar sync: add your sessions to Google, Apple or Outlook' },
    jc && { key: 'offers', label: 'Offers', Icon: TicketPercent, to: `/p/${jc}?sheet=offers` },
    { key: 'messages', label: 'Messages', Icon: Inbox, to: '/notifications' },
    { key: 'profile', label: 'Profile', initials: initialsFrom(user?.profile?.fullName || user?.email || ''), to: '/my-profile' },
    { key: 'settings', label: 'Settings', Icon: SettingsIcon, to: '/client/settings' },
  ].filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-[60]"
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
    >
      {/* Scrim */}
      <div
        onClick={onClose}
        className="absolute inset-0"
        style={{
          backgroundColor: 'rgba(20, 18, 14, 0.36)',
          opacity: entered ? 1 : 0,
          transition: `opacity ${SHEET_MS}ms ease-out`,
        }}
      />

      {/* Sheet */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="absolute left-0 right-0 bottom-0 mx-auto"
        style={{
          maxWidth: 520,
          backgroundColor: 'var(--bg-elev)',
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          boxShadow: 'var(--shadow-sheet)',
          transform: entered ? 'translateY(0)' : 'translateY(100%)',
          transition: `transform ${SHEET_MS}ms ease-out`,
          maxHeight: '88vh',
          overflowY: 'auto',
          padding: '0 16px calc(20px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {/* Grab handle */}
        <div className="flex justify-center pt-2.5 pb-3">
          <div style={{ width: 40, height: 5, borderRadius: 999, backgroundColor: 'var(--line-dark)' }} />
        </div>

        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-2 right-2 flex items-center justify-center rounded-full hover:bg-paper-deep"
          style={{ width: 48, height: 48, color: 'var(--ink-3)' }}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Book — the one primary */}
        <button
          type="button"
          onClick={() => go('/book')}
          className="av-book-pill"
          style={{ width: '100%', border: 'none', marginTop: 26, cursor: 'pointer' }}
        >
          Book a session
        </button>

        {/* Everything else, as labeled icons */}
        <div className="av-tiles" style={{ marginTop: 14 }}>
          {tiles.map(t => (
            <button
              key={t.key}
              type="button"
              className="av-tile"
              onClick={() => go(t.to)}
              aria-label={t.aria || undefined}
            >
              {t.initials
                ? <span className="av-initials" aria-hidden="true">{t.initials}</span>
                : <t.Icon size={26} strokeWidth={1.75} aria-hidden="true" />}
              {t.label}
              {t.count != null && <span className="av-count" aria-hidden="true">{t.count}</span>}
              {t.dot && <span className="av-dot" aria-hidden="true" />}
            </button>
          ))}
        </div>

        {/* Passwordless door + sign out. */}
        <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
          {user && user.hasPassword === false && (
            <button
              type="button"
              onClick={() => setUpgradeOpen(true)}
              className="av-chip"
              style={{ width: '100%', justifyContent: 'center', color: 'var(--accent-ink)' }}
            >
              <Lock className="w-5 h-5" aria-hidden="true" />
              Add email & password
            </button>
          )}
          {isTestAccount && (
            <button
              type="button"
              onClick={handleKillSwitch}
              className="av-chip"
              style={{ width: '100%', justifyContent: 'center', color: 'var(--danger, #A54641)' }}
            >
              <Trash2 className="w-5 h-5" aria-hidden="true" />
              Kill switch — delete this test account
            </button>
          )}
          <button
            type="button"
            onClick={handleSignOut}
            className="av-chip"
            style={{ width: '100%', justifyContent: 'center', background: 'transparent' }}
          >
            <LogOut className="w-5 h-5" aria-hidden="true" />
            Sign out
          </button>
        </div>
      </div>

      {/* Shared credentials surface, stacked above this sheet. */}
      <AccountUpgrade
        variant="sheet"
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        context="settings"
      />
    </div>
  );
}
